import React from 'react';
import i18n from '@i18next/i18n';
import { IReactionDisposer, reaction } from 'mobx';
import {
    IMobileRatingService,
} from '@src/slide/application/service/slide-rating-and-improve/mobile-slide-rating-service.interface';
import { SlideContentAnalyzer } from '@src/slide/application/service/slide-rating-and-improve/slide-content-analyzer';
import { IUiDataService } from '@src/slide/domain/ui-model/service/ui-data-service.interface';
import {
    ISlideContentEnumeratorService,
} from '@src/slide/application/service/slide-content-enumerator/slide-content-enumerator-service.interface';
import {
    ISlideContentLayoutAnalyzerService,
} from '@src/slide/application/service/slide-content-layout-analyzer/core/slide-content-layout-analyzer-service.interface';
import PullPanel from '@tencent/dui-mobile/lib/components/PullPanel';
import LayoutHeader from '@com-m/pull-panel-m/layout-header';
import { ISelectionService } from '@src/slide/application/service/selection/selection-service.interface';
import {
    PullPanelContent,
    PullPanelScene,
} from '@src/slide/application/service/slide-rating-and-improve/view/mobile/result/pull-panel-content';
import {
    PANEL_HEADER_HEIGHT,
    START_AND_RESULT_HEIGHT,
} from '@src/slide/application/service/slide-rating-and-improve/view/mobile/result/constant';
import type MobileEntryStateService from '@src/slide/application/service/mobile-entry-state-service';
import {
    IMobileToolbarEditManager,
} from '@src/slide/application/service/mobile-toolbar-edit-manager/core/mobile-toolbar-edit-manager.interface';
import { EditPanelType } from '@com-m/edit-panel-m/const';
import { tdw } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/tdw-report';
import {
    TDWActionType,
    TDWModuleType,
    TDWOperationType,
} from '@src/slide/infrastructure/module/dependent/report/offline-data-report/constant';

export class MobileRatingService implements IMobileRatingService {
    public isWaitingForShowInEdit: boolean;
    private analyzer: SlideContentAnalyzer;
    private isShow: boolean;
    private panel: any; // PullPanel组件返回的实例
    private editStateDisposer: IReactionDisposer;
    public constructor(
        uiDataService: IUiDataService,
        private selectionService: ISelectionService,
        private mobileEntryStateService: MobileEntryStateService,
        private mobileToolbarEditManager: IMobileToolbarEditManager,
        slideContentEnumerator: ISlideContentEnumeratorService,
        contentLayoutAnalyzer: ISlideContentLayoutAnalyzerService,
    ) {
        this.analyzer = new SlideContentAnalyzer(
            { forbidFontUseNumber: true },
            uiDataService, slideContentEnumerator,
            contentLayoutAnalyzer, this.showUniformFontUI,
        );
    }

    public getAnalyser() {
        return this.analyzer;
    }

    /**
     * 在移动端预览页展示评分功能
     */
    public showInPreview() {
        if (this.isShow) return;
        this.isShow = true;
        const panel = PullPanel.show({
            pullable: false,
            panelHeight: window.screen.height / 2,
            headerStyle: { height: `${PANEL_HEADER_HEIGHT}px`, padding: '0 0' },
            header: <LayoutHeader
                onClose={this.handleOnClose}
                title={i18n.t('排版检查和建议')}>
            </LayoutHeader>,
            content: <PullPanelContent
                scene={PullPanelScene.Preview}
                analyzer={this.analyzer}
                mobileRatingService={this}
                selectionService={this.selectionService}
                getPanelFunc={() => this.panel }
                handleToOptimize={this.handleOnToOptimizeInPreview}
                handleOnClose={this.handleOnClose}
            />,
        });
        this.panel = panel;
    }

    /**
     * 在移动端编辑页展示评分功能
     */
    public showInEdit() {
        if (!this.mobileEntryStateService.checkIfEditMode?.()) {
            this.mobileEntryStateService.enterEditMode?.();
        }
        setTimeout(() => {
            this.mobileToolbarEditManager.openEditPanel(null, EditPanelType.LAYOUT_SUGGESTION);
        }, 10);
    }

    /**
     * 编辑页切换到标题预览状态
     * @param titlePanel
     */
    public updateEditState(titlePanel: PullPanel) {
        this.panel = titlePanel;
    }

    public getSelectionService(): ISelectionService {
        return this.selectionService;
    }

    private handleOnClose = (withReset = true) => {
        if (withReset) {
            this.analyzer.reset();
        }
        this.panel.close();
        this.isShow = false;
        this.panel = null;
    };

    private showUniformFontUI = (onHandled: () => void, onCancel: () => void) => {
        // 等待后续阶段性产品需求
        return;
    };

    /**
     * 预览页点击去优化，引导进入编辑页
     */
    private handleOnToOptimizeInPreview = () => {
        this.handleOnClose(false);
        this.isWaitingForShowInEdit = true;
        this.showInEdit();
        tdw({
            opername: TDWOperationType.DOC_PPT,
            module: TDWModuleType.AUTO_BEAUTIFY,
            action: TDWActionType.BEAUTIFY_VIEW_TO_EDIT,
        });
    };
}
